import { Grid, Menu, MenuItem } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Location } from '@nx-smartmonkey/shared/domain';
import { RoutalLoader } from '@nx-smartmonkey/ui';
import { PlanDetailsMapContextAnchor } from '.';

import { useUpdateDriver } from '../../../../hooks/driver/useUpdateDriver';
import { useRetrieveActivePlan } from '../../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../../hooks/plan/useRetrieveActivePlanStops';
import { useSetActivePlanSelectedDrivers } from '../../../../hooks/plan/useSetActivePlanSelectedDrivers';
import { useSetActivePlanSelectedStops } from '../../../../hooks/plan/useSetActivePlanSelectedStops';
import { useRetrieveActiveProjectResources } from '../../../../hooks/project/useRetrieveActiveProjectResources';
import { useUpdateStop } from '../../../../hooks/stop/useUpdateStop';
import Text from '../../../../react-components/Text';

const styles = () => ({
  rootMenu: {
    width: `240px`,
  },
  menuItem: {
    height: `42px`,
  },
});

interface MapContextMenuProps {
  classes: {
    rootMenu: string;
    menuItem: string;
  };
  contextAnchor: PlanDetailsMapContextAnchor;
  selectedLocation?: Location;
  closeContextMenu: () => void;
  openCreateStopsDialog: (open: boolean) => void;
}

export const MapContextMenu = withStyles(styles)(
  ({ classes, contextAnchor, selectedLocation, closeContextMenu, openCreateStopsDialog }: MapContextMenuProps) => {
    const { data: activePlan } = useRetrieveActivePlan();
    const { data: projectResources } = useRetrieveActiveProjectResources();

    const { run: updateStop } = useUpdateStop();
    const { run: updateDriver } = useUpdateDriver();

    const { data: stops, isLoading: isStopsLoading } = useRetrieveActivePlanStops();
    const { data: drivers, isLoading: isDriversLoading } = useRetrieveActivePlanDrivers();

    const { selectedStops } = useSetActivePlanSelectedStops();
    const { selectedDrivers } = useSetActivePlanSelectedDrivers();

    if (isStopsLoading || isDriversLoading) {
      return (
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          style={{ height: `100%`, width: `calc(100% - 380px)` }}
        >
          <RoutalLoader width={60} />
        </Grid>
      );
    }

    if (!activePlan) {
      return null;
    }

    return (
      <Menu
        onClose={() => {
          // setContextAnchor(undefined);
          closeContextMenu();
        }}
        open={Boolean(contextAnchor)}
        anchorReference="anchorPosition"
        anchorPosition={contextAnchor && contextAnchor.screenPosition}
        classes={{ list: classes.rootMenu }}
      >
        {selectedLocation === undefined && <RoutalLoader />}
        {selectedLocation === null && (
          <Text variant="body" tagName="p" style={{ padding: `16px` }} intlId="location_nothing_found" />
        )}
        {selectedLocation && (
          <>
            <p style={{ padding: `16px`, fontSize: `11px` }}>{selectedLocation.label}</p>

            <MenuItem
              className={classes.menuItem}
              disabled={(stops ?? []).length >= (projectResources?.max_stops_plan ?? 0)}
              onClick={() => {
                closeContextMenu();
                openCreateStopsDialog(true);
              }}
            >
              <Text variant="body" style={{ cursor: `pointer` }} intlId="properties.service.map.create_new" />
            </MenuItem>
            {selectedStops && selectedStops.length === 1 && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  const stop = stops?.find((stop) => stop.id === selectedStops[0]);
                  if (stop) {
                    updateStop({
                      stop,
                      changes: {
                        location: selectedLocation,
                      },
                    });
                  }
                  closeContextMenu();
                }}
              >
                <Text variant="body" intlId="properties.service.map.add_to_current" />
              </MenuItem>
            )}
            {selectedDrivers && selectedDrivers.length === 1 && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  const driver = drivers?.find((driver) => driver.id === selectedDrivers[0]);
                  if (driver) {
                    updateDriver({
                      driver,
                      changes: {
                        start_location: selectedLocation,
                      },
                    });
                  }
                  closeContextMenu();
                }}
              >
                <Text variant="body" style={{ cursor: `pointer` }} intlId="properties.route.map.add_to_current_start" />
              </MenuItem>
            )}
            {selectedDrivers && selectedDrivers.length === 1 && (
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  const driver = drivers?.find((driver) => driver.id === selectedDrivers[0]);
                  if (driver) {
                    updateDriver({
                      driver,
                      changes: {
                        end_location: selectedLocation,
                      },
                    });
                  }
                  closeContextMenu();
                }}
              >
                <Text variant="body" style={{ cursor: `pointer` }} intlId="properties.route.map.add_to_current_end" />
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    );
  }
);
